import { useRef, useCallback, useEffect } from 'react';

/**
 * useAlarmVoice
 * Voz robótica con Web Speech API — sin dependencias externas.
 *
 * Uso:
 *   const { speak, stopVoice } = useAlarmVoice();
 *   speak('Alerta crítica: host caído');
 *
 * Opciones:
 *   lang    — idioma ('es-MX' | 'es-ES' | 'en-US')
 *   rate    — velocidad 0.1–2.0  (default 0.85, más lento = más robótico)
 *   pitch   — tono    0.0–2.0  (default 0.6, más bajo = más grave/robótico)
 *   volume  — volumen 0.0–1.0  (default 1.0)
 *   repeat  — repetir en loop  (default false)
 *   repeatDelay — ms entre repeticiones (default 3000)
 */
const useAlarmVoice = (options = {}) => {
  const {
    lang         = 'es-MX',
    rate         = 0.85,
    pitch        = 0.6,
    volume       = 1.0,
    repeat       = false,
    repeatDelay  = 3000,
  } = options;

  const intervalRef  = useRef(null);
  const isSpeaking   = useRef(false);

  /* ── Verifica soporte ───────────────────────────────────── */
  const isSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  /* ── Detener voz ────────────────────────────────────────── */
  const stopVoice = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isSpeaking.current = false;
  }, [isSupported]);

  /* ── Hablar una vez ─────────────────────────────────────── */
  const speakOnce = useCallback((text) => {
    if (!isSupported || !text) return;

    // Cancela cualquier utterance anterior
    window.speechSynthesis.cancel();

    const utterance        = new SpeechSynthesisUtterance(text);
    utterance.lang         = lang;
    utterance.rate         = rate;
    utterance.pitch        = pitch;
    utterance.volume       = volume;

    // Intentamos usar una voz local del sistema para que suene más robótica
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
  v.lang.startsWith(lang.split('-')[0]) &&
  v.localService &&
  (v.name.includes('female') || v.name.includes('Paulina') || v.name.includes('Sabina'))
) || voices.find(v =>
  v.lang.startsWith(lang.split('-')[0]) &&
  (v.name.includes('female') || v.name.includes('Paulina') || v.name.includes('Sabina'))
) || voices.find(v =>
  v.lang.startsWith(lang.split('-')[0]) && v.localService
) || voices.find(v =>
  v.lang.startsWith(lang.split('-')[0])
);

    if (preferred) utterance.voice = preferred;

    window.speechSynthesis.speak(utterance);
  }, [isSupported, lang, rate, pitch, volume]);

  /* ── speak: una vez o en loop ───────────────────────────── */
  const speak = useCallback((text) => {
    if (!isSupported) return;
    isSpeaking.current = true;

    speakOnce(text);

    if (repeat) {
      intervalRef.current = setInterval(() => {
        if (!isSpeaking.current) return;
        speakOnce(text);
      }, repeatDelay);
    }
  }, [isSupported, speakOnce, repeat, repeatDelay]);

  /* ── Cleanup al desmontar ───────────────────────────────── */
  useEffect(() => {
    return () => stopVoice();
  }, [stopVoice]);

  return { speak, stopVoice, isSupported };
};

export default useAlarmVoice;